/* eslint-disable jsx-a11y/no-autofocus */
import { styled } from 'styled-components';

import { theme } from '../../styles';
import { CancelIcon, EditIcon } from '../../assets/icons';
import { NotificationBubbleIllustration } from '../../assets/illustrations';
import { Button } from '../Button';
import { ErrorAlert } from '../ErrorAlert';
import { InputField, RadioButton } from '../Form';
import { Headings } from '../Headings';
import { Container, ResponsiveSpacer, VerticalSpacer } from '../Layout';
import { Line } from '../Line';
import { Text } from '../Text';
import { PrefChannel } from './types';

const IconWrapper = styled.div<{ displayIcon: boolean }>`
  position: absolute;
  top: -1.25rem;
  ${props => !props.displayIcon && 'display: none'};
`;

type ContactCardProps = {
  /** Hide or show NotificationBubble illustration */
  hasIcon?: boolean;
  /** Email useInputField/useInputWithRedux prop */
  emailInput: any;
  /** Phone useInputField/useInputWithRedux prop */
  phoneInput: any;
  /** Dispatchs the changePrefChannel action in /client */
  onChangePrefChannel: (prefChannel: PrefChannel) => any;
  /** Dispachs the cancelEditContactInfo action */
  onCancel: () => void;
  /** Triggers function to save Contact information */
  onSave: () => void;
  /** Prop to send contactInfo.prefChannel */
  currentPrefChannel: PrefChannel;
  /** Prop to send the contactInfo.editedPrefChannel */
  editedPrefChannel: PrefChannel;
  /** Prop to send boolean from contactInfo.isEditing */
  isEditing: boolean;
  /** dispatch editContactInfo */
  changeEditContactInfo: () => void;
  /** Triggers error on radio buttons of Contact Card */
  setRadioError?: boolean;
  /** Changes the variant of the Contact Card, options available 'radio' and 'fields' */
  variant?: 'radio' | 'fields';
  /** Sends contactInfo selector */
  contactInfo: any;
};

export const ContactCard = ({
  hasIcon = true,
  emailInput,
  phoneInput,
  onChangePrefChannel,
  onCancel,
  onSave,
  currentPrefChannel,
  editedPrefChannel,
  isEditing,
  changeEditContactInfo,
  setRadioError,
  variant,
  contactInfo,
}: ContactCardProps) => {
  const isPrefChannelUnknown = currentPrefChannel === PrefChannel.UNKNOWN;
  const isValidEmail = emailInput.value && emailInput.errors.length === 0;
  const isValidPhoneNumber = phoneInput.value && phoneInput.errors.length === 0;
  const onChangeInput =
    contactInfo.phone.editedNumber !== contactInfo.phone.number ||
    contactInfo.email.editedValue !== contactInfo.email.value;

  const onlyFields = variant === 'fields';

  const isInvalidContactInfo = () => {
    if ((editedPrefChannel === PrefChannel.EMAIL || onlyFields) && isValidEmail) {
      return emailInput.hasError;
    }
    if ((editedPrefChannel === PrefChannel.SMS || onlyFields) && isValidPhoneNumber) {
      return phoneInput.hasError;
    }
    return true;
  };

  const clearInputErrors = () => {
    emailInput.clearErrors();
    phoneInput.clearErrors();
  };

  const renderContactOptions = () => (
    <>
      <Text marginBottom={20}>Hur vill du bli kontaktad?</Text>
      <RadioButton
        id="radio-button-pref-channel-email"
        name="contact-info-radio-buttons"
        label="E-post"
        value={PrefChannel.EMAIL}
        onChange={() => {
          onChangePrefChannel(PrefChannel.EMAIL);
          phoneInput.clearErrors();
        }}
        checked={
          (currentPrefChannel === PrefChannel.EMAIL && editedPrefChannel !== PrefChannel.SMS) ||
          editedPrefChannel === PrefChannel.EMAIL
        }
        error={setRadioError && editedPrefChannel === PrefChannel.UNKNOWN}
      />
      <VerticalSpacer marginBottom={[30, 30, 20]} />
      <RadioButton
        id="radio-button-pref-channel-sms"
        name="contact-info-radio-buttons"
        label="SMS"
        value={PrefChannel.SMS}
        onChange={() => {
          onChangePrefChannel(PrefChannel.SMS);
          emailInput.clearErrors();
        }}
        checked={
          (currentPrefChannel === PrefChannel.SMS && editedPrefChannel !== PrefChannel.EMAIL) ||
          editedPrefChannel === PrefChannel.SMS
        }
        error={setRadioError && editedPrefChannel === PrefChannel.UNKNOWN}
      />
      {renderInputFields()}
    </>
  );

  const renderInputFields = () => (
    <>
      {(editedPrefChannel === PrefChannel.EMAIL || onlyFields) && (
        <>
          <VerticalSpacer marginBottom={onlyFields ? 0 : 40} />
          <InputField
            {...emailInput}
            autoFocus={!onlyFields}
            id="input-contact-info-email"
            autocomplete="email"
            type="email"
            label="E-postadress"
            errors={emailInput.errors}
            value={emailInput.value}
            onFocus={() => {
              emailInput.onFocus();
              emailInput.clearErrors();
            }}
            onBlur={() => {
              setTimeout(() => {
                emailInput.onBlur();
                if (isPrefChannelUnknown && isValidEmail) {
                  onSave();
                }
              }, 100);
            }}
          />
        </>
      )}
      {(editedPrefChannel === PrefChannel.SMS || onlyFields) && (
        <>
          <VerticalSpacer marginBottom={40} />
          <InputField
            {...phoneInput}
            autoFocus={!onlyFields}
            id="input-contact-info-mobile"
            autocomplete="tel"
            type="tel"
            label="Mobilnummer (SMS)"
            errors={phoneInput.errors}
            value={phoneInput.value}
            onFocus={() => {
              phoneInput.onFocus();
              phoneInput.clearErrors();
            }}
            onBlur={() => {
              setTimeout(() => {
                phoneInput.onBlur();
                if (isPrefChannelUnknown && isValidPhoneNumber) {
                  onSave();
                }
              }, 100);
            }}
          />
        </>
      )}
      {((!isPrefChannelUnknown && !onlyFields) || (onChangeInput && onlyFields)) && (
        <>
          <VerticalSpacer marginBottom={40} />
          <ResponsiveSpacer gutterWidth={40} gutterHeight={40} breakpoint={theme.breakpoint.md}>
            <Button
              testId="test-button-save-contact-info"
              fontSize="standard"
              onClick={onSave}
              disabled={(!onChangeInput && (!isValidPhoneNumber || !isValidEmail)) || isInvalidContactInfo()}
            >
              Spara ändringar
            </Button>
            <Button
              testId="test-button-cancel-editing-contact-info"
              fontSize="standard"
              styleAs="link"
              onClick={() => {
                onCancel();
                clearInputErrors();
              }}
            >
              <CancelIcon width={13} title="Avbryt" containerSize={22} />
              Avbryt
            </Button>
          </ResponsiveSpacer>
        </>
      )}
    </>
  );

  const renderCurrentContact = () => (
    <>
      <Text color="black70" marginRight={10} style={{ display: 'inline-block' }}>
        {currentPrefChannel === PrefChannel.EMAIL ? 'E-post:' : 'SMS:'}
      </Text>
      <Text style={{ display: 'inline-block' }}>
        {currentPrefChannel === PrefChannel.EMAIL ? emailInput.value : phoneInput.value}
      </Text>
      <Line color="black30" marginTop={30} marginBottom={20} />
      <Button
        testId="test-button-change-contact-info"
        styleAs="link"
        fontSize="standard"
        onClick={changeEditContactInfo}
      >
        <EditIcon containerSize={22} marginRight={5} />
        Ändra kontaktuppgifter
      </Button>
    </>
  );

  return onlyFields ? (
    <Container backgroundColor="white">{renderInputFields()}</Container>
  ) : (
    <Container
      backgroundColor="black05"
      paddingTop={30}
      paddingBottom={60}
      paddingRight={[20, 40, 40]}
      paddingLeft={[20, 40, 40]}
      width={['auto', 'auto', 658]}
      position="relative"
      borderRadius="2px"
    >
      <IconWrapper displayIcon={hasIcon}>
        <NotificationBubbleIllustration />
      </IconWrapper>
      {isPrefChannelUnknown && (
        <>
          <Headings.H4 marginBottom={5}>Lämna kontaktuppgifter</Headings.H4>
          <Text marginBottom={20}>
            Lämna e-postadress eller mobilnummer så att vi kan meddela dig när det finns ny information att ta del av.
          </Text>
          {renderContactOptions()}
          {setRadioError && editedPrefChannel === PrefChannel.UNKNOWN && (
            <ErrorAlert text="Välj ett av alternativen." />
          )}
        </>
      )}
      {!isPrefChannelUnknown && (
        <>
          <Headings.H4 marginBottom={20}>Vi håller dig uppdaterad</Headings.H4>
          {isEditing ? renderContactOptions() : renderCurrentContact()}
        </>
      )}
    </Container>
  );
};
